<template>
  <div>
    <v-dialog scrollable max-width="1200" v-model="model" persistent>
      <material-card
        :title="$t('enums.MemberTermination')"
        :color="$vuetify.theme.themes.light.secondary"
        icon="power_settings_new"
      >
        <v-card-text>
          <pbas-data-table
            ref="table"
            :dataProvider="provider"
            fileNamePrefix="member_termination"
            :rowActions="rowActions"
            identifier="MemberTerminationTable"
            :floatActions="floatActions"
          ></pbas-data-table>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="$emit('close')">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
        <v-dialog scrollable v-model="dialogs.edit" persistent max-width="800">
          <material-card
            v-if="dialogs.edit"
            :title="
              $t(`components.memberview.terminate_title_{0}`, [memberName])
            "
            icon="power_settings_new"
            :color="$vuetify.theme.themes.light.primary"
          >
            <v-card-text>
              <v-form v-model="valid">
                <div class="row">
                  <div class="col-12 col-md-4">
                    <!-- rename to termination date-->
                    <pbas-date-picker
                      :label="$t('headers.members_management.Effective Date')"
                      :value="editedItem.TerminationDate"
                      @update-date="editedItem.TerminationDate = $event"
                      :rules="[$rules.required, rules.terminationDate]"
                      class="required"
                      :allowedDates="allowedDates"
                    ></pbas-date-picker>
                  </div>
                  <div
                    class="col-12 col-md-4"
                    v-if="selectedPlan.PlanType === 'Pension'"
                  >
                    <trans-select
                      :item="editedItem"
                      propertyName="SourceOfInformation"
                      :items="options.Models.SourceOfInformation"
                      :label="$t('globals.source_of_information')"
                    ></trans-select>
                  </div>
                  <div class="col-12 col-md-4">
                    <trans-enum
                      :value.sync="editedItem.TerminationStatus"
                      :items="terminationReasons"
                      :label="$t('globals.reason')"
                      :rules="[$rules.required]"
                      class="required"
                    ></trans-enum>
                  </div>
                  <div
                    class="col-12 col-md-4"
                    v-if="
                      editedItem.TerminationStatus === 'Retired' ||
                      editedItem.TerminationStatus === 'Permanent Lay Off' ||
                      editedItem.TerminationStatus === 'Quit' ||
                      editedItem.TerminationStatus === 'Dismissal'
                    "
                  >
                    <v-checkbox
                      :disabled="smartEligibility"
                      v-model="editedItem.HasExtensions"
                      :label="$t('components.memberview.extension_of_benefits')"
                    />
                  </div>
                  <div
                    class="col-12 col-md-4"
                    v-if="editedItem.TerminationStatus === 'Deceased'"
                  >
                    <v-checkbox
                      v-model="editedItem.SurvivorBenefits"
                      :label="$t('components.memberview.survivor_benefits')"
                    />
                  </div>
                </div>
                <div class="row" v-if="editedItem.HasExtensions">
                  <div class="col-12 col-xs-12">
                    <extended-benefits-table
                      :extendedBenefits="computeExtendedBenefits()"
                      :terminationDate="editedItem.TerminationDate"
                      :memberIdentityId="memberIdentityId"
                      @changed-added="extendedBenefitsAdded($event)"
                      @changed-updated="extendedBenefitsUpdated($event)"
                      @changed-removed="extendedBenefitsRemoved($event)"
                      @change-all="editedItem.ExtensionClauses = $event"
                      :coverageIdentities="fixedCoverageIdentities"
                      :smartEligibility="smartEligibility"
                    />
                  </div>
                </div>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn
                color
                text
                @click="
                  dialogs.edit = false;
                  editedItem = null;
                "
              >
                {{ $t("globals.cancel") }}
              </v-btn>
              <v-btn
                :disabled="!valid"
                :loading="saving"
                color="primary"
                @click="saveRow"
              >
                {{ $t("globals.save") }}
              </v-btn>
            </v-card-actions>
          </material-card>
        </v-dialog>
        <v-dialog
          scrollable
          v-model="dialogs.delete"
          persistent
          max-width="400"
        >
          <material-card
            v-if="editedItem"
            :title="
              $t('components.member_view.member_termination.delete_row_{0}', [
                $helpers.formatDate(editedItem.EffectiveDate),
              ])
            "
            :color="$vuetify.theme.themes.light.error"
            icon="delete"
          >
            <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>
            <v-card-actions>
              <v-btn color text @click="dialogs.delete = false">
                {{ $t("globals.cancel") }}
              </v-btn>
              <v-btn color="error" text @click="deleteRow">
                {{ $t("globals.delete") }}
              </v-btn>
            </v-card-actions>
          </material-card>
        </v-dialog>
        <v-dialog scrollable v-model="loading" persistent max-width="200">
          <v-card>
            <v-card-title>{{ $t("globals.please_wait") }}</v-card-title>
            <v-card-text>
              <v-container fluid grid-list-md>
                <v-layout row justify-center>
                  <v-flex xs12 sm6 md4 lg3>
                    <v-progress-circular indeterminate></v-progress-circular>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
          </v-card>
        </v-dialog>
      </material-card>
    </v-dialog>
    <error-handler :error="error"></error-handler>
    <v-snackbar v-model="snackbar" color="success" :timeout="2500">
      Success
      <v-btn dark text @click="snackbar = false">
        {{ $t("globals.close") }}
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { InMemoryProvider } from "pbas-vue-components";
import MemberTerminationHeaders from "@/headers/MemberTermination";
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import ExtendedBenefitsTable from "@/components/ExtendedBenefitsTable.vue";
import TransEnum from "../../TransEnum.vue";

export default {
  props: [
    "model",
    "memberIdentityId",
    "items",
    "fixedCoverageIdentities",
    "addEnabled",
    "memberName",
    "smartEligibility",
  ],
  components: {
    ErrorHandler,
    ExtendedBenefitsTable,
    TransEnum,
  },
  computed: {
    ...mapGetters("common", { selectedPlan: "plan" }),
    ...mapGetters("common", ["settings"]),
    ...mapGetters("plansMembers", ["options", "planIdentity"]),
    floatActions() {
      if (this.addEnabled) {
        return [
          {
            name: "addItem",
            icon: "add",
            color: this.$vuetify.theme.themes.light.success,
            text: "globals.add_new",
            handler: () => {
              this.editHandler(true);
            },
          },
        ];
      }
      return [];
    },
    terminationReasons() {
      if (this.planIdentity.HasEligibilityBank) {
        return this.options.Enums.TerminationStatus.filter(
          (o) => o !== "Out-Of-Benefits" && o !== "Transfer"
        );
      }
      return this.options.Enums.TerminationStatus.filter(
        (o) => o !== "Out-Of-Benefits"
      );
    },
  },
  data() {
    return {
      saving: false,
      loading: false,
      error: null,
      dialogs: {
        edit: false,
        delete: false,
      },
      provider: undefined,
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.editHandler(false, item);
          },
        },
        {
          name: "delete",
          icon: "delete",
          title: "globals.delete",
          handler: (item) => this.deleteHandler(item),
          visible: (item) => item.DeleteEnabled === true,
        },
      ],
      editedItem: null,
      valid: false,
      isNew: false,
      snackbar: false,
      rules: {
        terminationDate: (v) => {
          if (v === undefined || v === null || v === "") {
            return true;
          }

          if (!this.$helpers.isDate(v)) {
            return this.$t("validation.not_a_date");
          }

          const val = new Date(Date.parse(v));
          const startDate = Date.parse(this.editedItem.AllowedDates.StartDate);
          const start = new Date(startDate);
          if (
            this.editedItem.AllowedDates.EndDate !== null &&
            this.editedItem.AllowedDates.EndDate !== undefined
          ) {
            const endDate = Date.parse(this.editedItem.AllowedDates.EndDate);
            const end = new Date(endDate);
            return (
              (val >= start && val <= end) ||
              this.$t("validation.date_{0}_{1}", [
                this.$helpers.formatValidationDate(start),
                this.$helpers.formatValidationDate(end),
              ])
            );
          }
          return (
            val >= start ||
            this.$t("validation.date_{0}", [
              this.$helpers.formatValidationDate(start),
            ])
          );
        },
      },
    };
  },
  watch: {
    model: {
      handler(v) {
        // console.log(v);
        if (v) {
          this.loadData();
        } else {
          this.provider = undefined;
        }
      },
    },
    items: {
      handler() {
        this.loadData();
      },
    },
  },

  methods: {
    ...mapActions("plansMembers", [
      "getMemberTerminationByEffectiveDate",
      "terminate",
      "deleteTermination",
      "getMemberTerminationAllowedDates",
    ]),
    allowedDates(val) {
      const d = Date.parse(val);
      // const d = new Date(n);
      let start = Date.parse(this.editedItem.AllowedDates.StartDate);
      // const start = new Date(n2);
      if (
        this.editedItem.AllowedDates.EndDate !== null &&
        this.editedItem.AllowedDates.EndDate !== undefined
      ) {
        let end = Date.parse(this.editedItem.AllowedDates.EndDate);
        // const end = new Date(n3);
        return d >= start && d <= end;
      }
      return d >= start;
    },
    async loadData() {
      console.log("smart eligibility: ", this.smartEligibility);
      this.provider = undefined;
      if (this.items !== null && this.items !== undefined) {
        for (let item of this.items) {
          if (
            item.ExtensionClauses === null ||
            item.ExtensionClauses === undefined
          ) {
            item.ExtensionClauses = [];
          }
        }
        this.provider = new InMemoryProvider(
          this.items,
          MemberTerminationHeaders
        );
      }
    },
    computeExtendedBenefits() {
      // console.log(this.fixedCoverageIdentities);
      const extendedBenefits = this.editedItem.ExtensionClauses.filter(
        (o) => o !== null && o !== undefined && !o.IsRemoved
      ).map((e) => {
        let clause = e;
        // console.log(clause, e);
        clause.TerminatedExtendedCoverageIdentities =
          e.TerminatedExtendedCoverageIdentityIds.map((t) => {
            // console.log(t);
            if (t.Id) {
              return this.fixedCoverageIdentities.filter(
                (c) => c.Id === t.Id
              )[0];
            } else {
              return this.fixedCoverageIdentities.filter((c) => c.Id === t)[0];
            }
          });
        // console.log(clause.TerminatedExtendedCoverageIdentities);
        return clause;
      });
      return extendedBenefits;
    },
    extendedBenefitsUpdated(item) {
      if (item.Id) {
        let updated = this.editedItem.ExtensionClauses.filter(
          (o) => o.Id === item.Id
        )[0];
        console.log(updated);
        let index = this.editedItem.ExtensionClauses.indexOf(updated);
        this.editedItem.ExtensionClauses[index] = item;
      } else {
        let updated = this.editedItem.ExtensionClauses.filter(
          (o) => o.Identifier === item.Identifier
        )[0];
        let index = this.editedItem.ExtensionClauses.indexOf(updated);
        this.editedItem.ExtensionClauses[index] = item;
      }
      this.$forceUpdate();
    },
    extendedBenefitsAdded(item) {
      // this.$nextTick(()=>{
      console.log(item);
      this.editedItem.ExtensionClauses.push(item);
      // })

      this.$forceUpdate();
    },
    extendedBenefitsRemoved(item) {
      if (item.Id) {
        let removed = this.editedItem.ExtensionClauses.filter(
          (o) => o.Id === item.Id
        )[0];
        removed.EffectiveDate = item.EffectiveDate;
        removed.IsRemoved = true;
      } else {
        let removed = this.editedItem.ExtensionClauses.filter(
          (o) => o.Identifier === item.Identifier
        )[0];
        removed.EffectiveDate = item.EffectiveDate;
        removed.IsRemoved = true;
      }
      this.$forceUpdate();
    },
    async editHandler(isNew, row) {
      this.isNew = isNew;
      if (this.isNew) {
        try {
          this.loading = true;
          let item;
          item = (
            await this.getMemberTerminationAllowedDates({
              planIdentityId: this.selectedPlan.Id,
              memberIdentityId: this.memberIdentityId,
            })
          ).data;

          this.editedItem = {
            ExtensionClauses: [],
            AllowedDates: item,
          };
          this.dialogs.edit = true;
        } catch (error) {
          this.error = error;
        } finally {
          this.loading = false;
        }
      } else {
        try {
          this.loading = true;
          let item;
          item = (
            await this.getMemberTerminationByEffectiveDate({
              planIdentityId: this.selectedPlan.Id,
              memberIdentityId: this.memberIdentityId,
              date: row.EffectiveDate,
            })
          ).data;
          if (
            item.ExtensionClauses === null ||
            item.ExtensionClauses === undefined
          ) {
            item.ExtensionClauses = [];
          }
          this.editedItem = item;

          this.dialogs.edit = true;
        } catch (error) {
          this.error = error;
        } finally {
          this.loading = false;
        }
      }
    },
    async deleteHandler(row) {
      this.editedItem = row;
      this.dialogs.delete = true;
    },
    async deleteRow() {
      this.saving = true;
      try {
        await this.deleteTermination({
          planIdentityId: this.selectedPlan.Id,
          memberIdentityId: this.memberIdentityId,
          date: this.editedItem.EffectiveDate,
        });
        this.snackbar = true;
        this.editedItem = null;
        this.dialogs.delete = false;
        this.$emit("reload");
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },
    async saveRow() {
      this.saving = true;
      try {
        const item = JSON.parse(JSON.stringify(this.editedItem));

        if (item.SourceOfInformation) {
          item.SourceOfInformationId = item.SourceOfInformation.Id;
          delete item.SourceOfInformation;
        }
        item.MemberIdentityId = this.memberIdentityId;
        // delete item["@odata.context"];
        delete item.MemberGroupIdentity;
        delete item.ExtensionOfBenefits;
        delete item.Class;
        if (item.HasExtensions) {
          if (item.ExtensionClauses) {
            for (let c of item.ExtensionClauses) {
              delete c.Identity;
              let terminatedCoverageIds =
                c.TerminatedExtendedCoverageIdentityIds.map((o) => {
                  if (o.Id) {
                    return o.Id;
                  } else {
                    return o;
                  }
                });
              c.TerminatedExtendedCoverageIdentityIds = terminatedCoverageIds;
              delete c.TerminatedExtendedCoverageIdentities;
            }
          }
        } else {
          delete item.ExtensionClauses;
        }

        await this.terminate({
          planIdentityId: this.selectedPlan.Id,
          payload: item,
        });
        this.snackbar = true;
        this.editedItem = null;
        this.dialogs.edit = false;
        this.$emit("reload");
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style>
.centered-div {
  margin-left: auto;
  margin-right: auto;
}
</style>
